import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import { testIdAttr } from '@src/components/testId';
import type { TypeRole } from '@src/components/Typography/roles';
import './Typography.css';

export type { TypeRole } from '@src/components/Typography/roles';
export { TYPE_GROUPS, TYPE_ROLES, TYPE_RULES } from '@src/components/Typography/roles';

/**
 * Font-size step used when a role's spec is a range (md–xl, xl–sm, xs–md).
 * Named after the token, so a call site never writes a pixel value.
 */
export type TypeSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export type TypeColor = 'role' | 'inherit';

type TypographyOwnProps = {
  role: TypeRole;
  /**
   * `role` (default) paints the role's colour. `inherit` keeps a painted
   * parent's ink — a primary button, a tinted card, a selected tag.
   */
  color?: TypeColor;
  /** Only for roles whose spec is a range. Ignored by fixed roles. */
  size?: TypeSize;
  /** Tabular figures — a counter, a session id, a clock. */
  tabular?: boolean;
  /** For copy a test needs to read — a status, a revealed name. */
  testId?: string;
  children?: ReactNode;
};

type NativeProps<E extends ElementType> = Omit<
  ComponentPropsWithoutRef<E>,
  // `role` and `size` are ours; `color` is 'role' | 'inherit', not a CSS colour.
  'as' | 'role' | 'color' | 'size'
>;

type TypographyProps<E extends ElementType> = TypographyOwnProps & {
  as?: E;
} & NativeProps<E>;

function roleClass(role: TypeRole): string {
  return `type--${role.replace('/', '-')}`;
}

/** The class list a role produces — for the rare native control that cannot wrap. */
export function typeClassName(
  role: TypeRole,
  options: { color?: TypeColor; size?: TypeSize } = {},
): string {
  const classes = ['type', roleClass(role)];
  if (options.color === 'inherit') {
    classes.push('type--color-inherit');
  }
  if (options.size) {
    classes.push(`type--size-${options.size}`);
  }
  return classes.join(' ');
}

/**
 * A single line of copy, styled as one type role. Screens and components pass
 * a role instead of picking size, weight and colour themselves.
 */
export function Typography<E extends ElementType = 'span'>({
  role,
  as,
  color = 'role',
  size,
  tabular = false,
  className,
  testId,
  ...rest
}: TypographyProps<E>) {
  const Tag = as ?? 'span';
  const classes = [typeClassName(role, { color, size })];
  if (tabular) {
    classes.push('type--tabular');
  }
  if (className) {
    classes.push(className);
  }

  return <Tag className={classes.join(' ')} {...testIdAttr(testId)} {...rest} />;
}
